import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ShieldCheck, Award, BadgeDollarSign, Clock, Building2, Phone } from 'lucide-react';
import { aboutUs, businessInfo } from '../mock';

const iconList = [Star, ShieldCheck, Award, BadgeDollarSign, Clock, Building2];

const AboutTextContent = ({ onQuote }) => (
  <div>
    <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
      {aboutUs.eyebrow}
    </span>
    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
      {aboutUs.title}
    </h2>
    <p className="text-lg text-slate-700 mb-5 leading-relaxed">{aboutUs.intro}</p>
    <p className="text-base text-slate-600 mb-5 leading-relaxed">{aboutUs.body}</p>
    <p className="text-base text-slate-600 leading-relaxed">{aboutUs.closing}</p>
    <div className="mt-8 flex flex-col sm:flex-row gap-4">
      <a
        href={`tel:${businessInfo.phoneRaw}`}
        data-testid="about-call-btn"
        className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
      >
        <Phone size={18} />
        Call {businessInfo.phone}
      </a>
      <button
        onClick={onQuote}
        data-testid="about-quote-btn"
        className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-300 hover:border-blue-600 hover:text-blue-600 text-slate-700 px-7 py-3.5 rounded-lg font-semibold transition-all duration-200"
      >
        Get a Free Quote
      </button>
    </div>
  </div>
);

const AboutImagePanel = () => (
  <div className="relative">
    <div className="relative rounded-2xl overflow-hidden shadow-xl">
      <img
        src="https://customer-assets.emergentagent.com/job_plumb-roofing-expert/artifacts/k07vkjr0_IMG_7372.JPG"
        alt="AA Plumbing and Roofing team at work"
        className="w-full h-[560px] object-cover"
      />
    </div>
    <div className="hidden md:block absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-6 border border-slate-100">
      <div className="text-4xl font-bold text-blue-600 mb-1">15+</div>
      <div className="text-sm text-slate-600 font-medium">Years of<br />Experience</div>
    </div>
    <div className="hidden md:block absolute -top-8 -right-8 bg-white rounded-2xl shadow-xl p-6 border border-slate-100">
      <div className="text-4xl font-bold text-red-600 mb-1">5.0★</div>
      <div className="text-sm text-slate-600 font-medium">Google<br />Rated</div>
    </div>
    <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-100 rounded-full opacity-60 blur-3xl -z-10" />
    <div className="absolute -top-6 -left-6 w-32 h-32 bg-red-100 rounded-full opacity-60 blur-3xl -z-10" />
  </div>
);

const AboutHero = ({ onQuote }) => (
  <div className="grid lg:grid-cols-2 gap-14 items-center mb-20">
    <AboutTextContent onQuote={onQuote} />
    <AboutImagePanel />
  </div>
);

const StatsBar = () => (
  <div className="bg-slate-50 rounded-2xl p-8 md:p-10 mb-20 border border-slate-200">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
      {aboutUs.stats.map((stat, index) => (
        <div key={stat.label} className="text-center" data-testid={`about-stat-${index}`}>
          <div className="text-3xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
          <div className="text-slate-600 text-sm md:text-base font-medium">{stat.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const HighlightCard = ({ highlight, index }) => {
  const Icon = iconList[index] || ShieldCheck;
  return (
    <div
      data-testid={`about-highlight-${index}`}
      className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-md transition-all duration-300"
    >
      <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
        <Icon className="text-blue-600" size={24} />
      </div>
      <h4 className="text-lg font-bold text-slate-900 mb-2">{highlight.title}</h4>
      <p className="text-slate-600 text-sm leading-relaxed">{highlight.description}</p>
    </div>
  );
};

const HighlightsGrid = () => (
  <div>
    <div className="text-center mb-12">
      <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
        Why Choose Us
      </span>
      <h3 className="text-3xl md:text-4xl font-bold text-slate-900">What Sets Us Apart</h3>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {aboutUs.highlights.map((highlight, index) => (
        <HighlightCard key={highlight.title} highlight={highlight} index={index} />
      ))}
    </div>
  </div>
);

const AboutSection = () => {
  const navigate = useNavigate();
  return (
    <section id="about" data-testid="about-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AboutHero onQuote={() => navigate('/contact')} />
        <StatsBar />
        <HighlightsGrid />
      </div>
    </section>
  );
};

export default AboutSection;
